
import { useLocation, Link } from "react-router-dom";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Mail, CheckCircle2, AlertTriangle, Clock, LogIn } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { useResendRateLimit } from "@/hooks/useResendRateLimit";

const SignupVerifyNotice = () => {
  const location = useLocation();
  const { toast } = useToast();
  const email = location.state?.email || "";
  const [isResending, setIsResending] = useState(false);
  
  const {
    isRateLimited,
    isLimitReached,
    remainingAttempts,
    recordSendAttempt,
    getStatusMessage,
    formatRemainingTime
  } = useResendRateLimit('verification');

  const handleResendVerification = async () => {
    if (!email || isRateLimited || isLimitReached) return;
    
    setIsResending(true);
    try {
      const { error } = await supabase.auth.signUp({ email, password: 'dummy-password' });
      if (error && !error.message.includes('already registered') && !error.message.includes('User already exists')) throw error;
      
      recordSendAttempt();
      
      toast({
        title: "驗證信已重新寄送",
        description: "請至您的信箱收信，並點擊驗證連結完成註冊。",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "驗證信寄送失敗",
        description: error.message,
      });
    } finally {
      setIsResending(false);
    }
  };

  const statusMessage = getStatusMessage();
  const timeInfo = formatRemainingTime();

  return (
    <MainLayout>
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-orange-50 to-white py-12 px-4">
        <div className="w-full max-w-md space-y-6 rounded-xl bg-white p-6 shadow-lg text-center border border-orange-200">
          <div className="flex flex-col items-center">
            <div className="h-16 w-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-8 w-8 text-orange-600" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">請確認您的電子郵件</h1>
            <p className="text-gray-600 text-sm leading-relaxed">
              我們已發送驗證郵件至下方地址，請點擊信件中的連結完成註冊
            </p>
          </div>
          
          <div className="bg-orange-50 border border-orange-200 rounded-lg py-3 px-4 flex items-center justify-center">
            <span className="font-medium text-orange-800 break-all text-sm">{email}</span>
          </div>
          
          <div className="space-y-4 pt-2">
            <div className="flex items-start space-x-3 text-left bg-blue-50 rounded-lg p-4 border border-blue-100">
              <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-blue-700 text-sm mb-1">驗證步驟</h3>
                <p className="text-blue-600 text-xs leading-relaxed">
                  點擊郵件中的驗證連結後，您將可以順利完成註冊流程。
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 text-left bg-amber-50 rounded-lg p-4 border border-amber-100">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-amber-700 text-sm mb-1">沒收到郵件？</h3>
                <p className="text-amber-600 text-xs leading-relaxed">
                  請檢查您的垃圾郵件或垃圾信箱，或者點擊下方按鈕重新寄送驗證信。
                </p>
              </div>
            </div>

            {(isRateLimited || isLimitReached) && statusMessage && (
              <div className="flex items-start space-x-3 text-left bg-red-50 rounded-lg p-4 border border-red-100">
                <Clock className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-red-700 text-sm mb-1">發送限制</h3>
                  <p className="text-red-600 text-xs leading-relaxed">{statusMessage}</p>
                  {!isLimitReached && isRateLimited && (
                    <p className="text-red-500 text-xs mt-1">
                      請等待 {timeInfo.formatted} 後再次嘗試
                    </p>
                  )}
                  {!isLimitReached && (
                    <p className="text-red-500 text-xs mt-1">
                      今日剩餘發送次數：{remainingAttempts}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-3 pt-4">
            <Button
              variant="outline"
              size="default"
              onClick={handleResendVerification}
              disabled={isResending || isRateLimited || isLimitReached}
              className="w-full py-3 text-sm font-medium border-orange-400 hover:bg-orange-50 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? (
                <>
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-orange-600 border-t-transparent"></span>
                  重新寄送中...
                </>
              ) : (
                "重新寄送驗證郵件"
              )}
            </Button>

            <Link to="/login" className="w-full block">
              <Button 
                variant="default" 
                size="default" 
                className="w-full py-3 text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white transition-all duration-200"
              >
                <LogIn className="mr-2 h-4 w-4" />
                已有帳號？立即登入
              </Button>
            </Link>
          </div>
          
          <div className="text-gray-500 pt-4 border-t border-gray-100">
            <p className="text-xs mb-1">如需協助，請聯繫我們的客服團隊</p>
            <p className="text-orange-600 font-medium text-xs">customer.service@trustay.app</p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SignupVerifyNotice;
